/**
 * CES Banner Web Component
 * 实现 CES Banner 的交互功能，包括弹窗控制
 * 遵循 SOLID 原则：单一职责、开闭原则
 */

(function() {
    'use strict';

    // 检查是否已经定义，防止重复注册
    if (customElements.get('ndev-ces-banner')) {
        return;
    }

    /**
     * NdevCesBanner Web Component
     * 负责管理 CES Banner 的交互逻辑
     */
    class NdevCesBanner extends HTMLElement {
        constructor() {
            super();
            this.button = null;
            this.popup = null;
            this.openPopupHandler = null;
            this.closePopupHandler = null;
            this.overlayClickHandler = null;
            this.escKeyHandler = null;
        }

        connectedCallback() {
            // 延迟初始化，确保 DOM 完全加载
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => this.init());
            } else {
                // 使用 setTimeout 确保所有脚本都已加载
                setTimeout(() => this.init(), 0);
            }
        }

        disconnectedCallback() {
            this.cleanup();
        }

        /**
         * 初始化视频背景
         * 延迟加载视频，先显示封面图，视频加载完成后自动播放
         */
        initVideoBackground() {
            const videoElement = this.querySelector('.bg-video');
            if (!videoElement) {
                return;
            }

            const posterElement = this.querySelector('.bg-video-poster');
            const sources = videoElement.querySelectorAll('source');
            
            if (sources.length === 0) {
                return;
            }

            // 根据屏幕尺寸选择视频源
            const isMobile = window.innerWidth <= 767;
            let selectedSource = null;
            
            sources.forEach(source => {
                const media = source.getAttribute('data-media');
                if (isMobile && media === 'mobile') {
                    selectedSource = source;
                } else if (!isMobile && media === 'desktop') {
                    selectedSource = source;
                }
            });

            // 如果没有找到匹配的源，使用第一个源
            if (!selectedSource && sources.length > 0) {
                selectedSource = sources[0];
            }

            if (!selectedSource) {
                return;
            }

            // 隐藏其他源，只显示选中的源
            sources.forEach(source => {
                if (source !== selectedSource) {
                    source.remove();
                }
            });

            // 确保视频元素可见
            videoElement.style.display = 'block';
            videoElement.style.visibility = 'visible';

            // 视频加载完成后自动播放
            videoElement.addEventListener('loadeddata', () => {
                videoElement.play().catch(err => {
                    console.warn('Video autoplay failed:', err);
                });
                
                // 视频开始播放后隐藏封面图
                if (posterElement) {
                    videoElement.addEventListener('playing', () => {
                        posterElement.style.opacity = '0';
                        posterElement.style.transition = 'opacity 0.5s ease-in-out';
                    }, { once: true });
                }
            }, { once: true });

            // 延迟加载视频（等待页面其他资源加载完成）
            if (document.readyState === 'complete') {
                setTimeout(() => this.loadVideo(videoElement), 500);
            } else {
                window.addEventListener('load', () => {
                    setTimeout(() => this.loadVideo(videoElement), 500);
                });
            }
        }

        /**
         * 加载视频
         */
        loadVideo(videoElement) {
            videoElement.load();
        }

        /**
         * 初始化组件
         */
        init() {
            // 初始化视频背景
            this.initVideoBackground();
            
            const buttonId = this.getAttribute('data-button-id');
            this.desktopImage = this.getAttribute('data-popup-desktop-image');
            this.mobileImage = this.getAttribute('data-popup-mobile-image');
            this.popupTitle = this.getAttribute('data-popup-title') || 'Explore the Narwal Booth';

            if (buttonId) {
                this.button = document.getElementById(buttonId);
            }

            if (!this.button) {
                console.warn('CES Banner: Button not found with ID:', buttonId);
                return;
            }

            // 检查是否有弹窗图片数据
            if (!this.desktopImage && !this.mobileImage) {
                console.warn('CES Banner: No popup images provided');
                return;
            }

            this.initPopup();
        }

        /**
         * 创建弹窗 DOM 结构（仅在第一次点击时调用）
         */
        createPopup(desktopImage, mobileImage) {
            // 创建弹窗容器
            this.popup = document.createElement('div');
            this.popup.className = 'ces-booth-popup';
            const componentId = this.getAttribute('id') || this.id;
            this.popup.id = `ces-booth-popup-${componentId || Date.now()}`;

            // 创建遮罩层
            const overlay = document.createElement('div');
            overlay.className = 'ces-booth-popup-overlay';
            this.popup.appendChild(overlay);

            // 创建内容容器
            const content = document.createElement('div');
            content.className = 'ces-booth-popup-content';

            // 创建关闭按钮
            const closeButton = document.createElement('button');
            closeButton.className = 'ces-booth-popup-close';
            closeButton.setAttribute('aria-label', '关闭弹窗');
            closeButton.type = 'button'; // 防止表单提交
            closeButton.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            `;
            content.appendChild(closeButton);

            // 创建标题
            const title = document.createElement('h2');
            title.className = 'ces-booth-popup-title';
            title.textContent = this.popupTitle;
            content.appendChild(title);

            // 创建地图容器
            const mapContainer = document.createElement('div');
            mapContainer.className = 'ces-booth-popup-map-container';

            // 创建桌面端图片
            if (desktopImage) {
                const desktopImg = document.createElement('img');
                desktopImg.src = desktopImage;
                desktopImg.alt = 'CES Booth Map';
                desktopImg.className = 'ces-booth-popup-image ces-booth-popup-image-desktop';
                mapContainer.appendChild(desktopImg);
            }

            // 创建移动端图片
            if (mobileImage) {
                const mobileImg = document.createElement('img');
                mobileImg.src = mobileImage;
                mobileImg.alt = 'CES Booth Map';
                mobileImg.className = 'ces-booth-popup-image ces-booth-popup-image-mobile';
                mapContainer.appendChild(mobileImg);
            }

            content.appendChild(mapContainer);
            this.popup.appendChild(content);

            // 将弹窗插入到组件内部
            this.appendChild(this.popup);
        }

        /**
         * 初始化弹窗功能
         */
        initPopup() {
            // 绑定按钮点击事件
            this.openPopupHandler = (e) => {
                if (e) {
                    e.preventDefault();
                }
                window.dataLayer.push({
                    event:"ga4Event",
                    event_name:"click_banner",
                    event_parameters:{
                        button_name: this.button.innerText,
                        module_name: "Ndev Ces Banner"
                    }
                });

                this.togglePopup();
            };

            if (this.button) {
                this.button.addEventListener('click', this.openPopupHandler);
            }

            // ESC 键关闭弹窗（全局监听）
            this.escKeyHandler = (e) => {
                if (e.key === 'Escape' && this.popup && this.popup.classList.contains('show')) {
                    this.closePopup();
                }
            };
            document.addEventListener('keydown', this.escKeyHandler);
        }

        /**
         * 切换弹窗显示/隐藏
         */
        togglePopup() {
            // 如果弹窗不存在，创建它并显示（第一次点击）
            if (!this.popup) {
                this.createPopup(this.desktopImage, this.mobileImage);
                this.bindPopupEvents();
                // 创建后直接显示
                this.openPopup();
                return;
            }

            // 如果弹窗已存在，切换显示/隐藏（第二次及以后点击）
            if (this.popup.classList.contains('show')) {
                this.closePopup();
            } else {
                this.openPopup();
            }
        }

        /**
         * 绑定弹窗事件（关闭按钮、遮罩层）
         */
        bindPopupEvents() {
            if (!this.popup) {
                return;
            }

            // 绑定关闭按钮事件
            const closeButton = this.popup.querySelector('.ces-booth-popup-close');
            if (closeButton && !this.closePopupHandler) {
                this.closePopupHandler = (e) => {
                    if (e) {
                        e.preventDefault();
                    }
                    this.closePopup();
                };
                closeButton.addEventListener('click', this.closePopupHandler);
            }

            // 点击遮罩层关闭弹窗
            const overlay = this.popup.querySelector('.ces-booth-popup-overlay');
            if (overlay && !this.overlayClickHandler) {
                this.overlayClickHandler = (e) => {
                    if (e.target === overlay) {
                        this.closePopup();
                    }
                };
                overlay.addEventListener('click', this.overlayClickHandler);
            }
        }

        /**
         * 打开弹窗（仅用于显示已存在的弹窗）
         */
        openPopup() {
            if (!this.popup) {
                return;
            }

            this.popup.classList.add('show');
            // document.body.style.overflow = 'hidden';
            
            // 触发重排，然后添加 visible 类以实现动画
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.popup.classList.add('visible');
                });
            });
        }

        /**
         * 关闭弹窗
         */
        closePopup() {
            if (!this.popup) {
                return;
            }

            this.popup.classList.remove('visible');
            
            // 等待动画完成后再移除 show 类
            setTimeout(() => {
                this.popup.classList.remove('show');
                document.body.style.overflow = '';
            }, 300);
        }

        /**
         * 清理资源
         */
        cleanup() {
            if (this.button && this.openPopupHandler) {
                this.button.removeEventListener('click', this.openPopupHandler);
            }

            const closeButton = this.popup?.querySelector('.ces-booth-popup-close');
            if (closeButton && this.closePopupHandler) {
                closeButton.removeEventListener('click', this.closePopupHandler);
            }

            const overlay = this.popup?.querySelector('.ces-booth-popup-overlay');
            if (overlay && this.overlayClickHandler) {
                overlay.removeEventListener('click', this.overlayClickHandler);
            }

            if (this.escKeyHandler) {
                document.removeEventListener('keydown', this.escKeyHandler);
            }

            this.button = null;
            this.popup = null;
            this.openPopupHandler = null;
            this.closePopupHandler = null;
            this.overlayClickHandler = null;
            this.escKeyHandler = null;
        }
    }

    // 注册 Web Component
    customElements.define('ndev-ces-banner', NdevCesBanner);

    // 兼容性处理：如果浏览器不支持 Custom Elements，使用传统方式初始化
    if (!window.customElements) {
        document.addEventListener('DOMContentLoaded', function() {
            const banners = document.querySelectorAll('.ndev-ces-banner[data-button-id]');
            banners.forEach(banner => {
                const buttonId = banner.getAttribute('data-button-id');
                const desktopImage = banner.getAttribute('data-popup-desktop-image');
                const mobileImage = banner.getAttribute('data-popup-mobile-image');
                
                if (buttonId) {
                    const button = document.getElementById(buttonId);
                    
                    if (button && (desktopImage || mobileImage)) {
                        // 创建弹窗（复用组件中的创建逻辑）
                        const popup = document.createElement('div');
                        popup.className = 'ces-booth-popup';
                        popup.id = `ces-booth-popup-${banner.id || Date.now()}`;

                        const overlay = document.createElement('div');
                        overlay.className = 'ces-booth-popup-overlay';
                        popup.appendChild(overlay);

                        const content = document.createElement('div');
                        content.className = 'ces-booth-popup-content';

                        const closeButton = document.createElement('button');
                        closeButton.className = 'ces-booth-popup-close';
                        closeButton.setAttribute('aria-label', '关闭弹窗');
                        closeButton.innerHTML = `
                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                                <circle cx="24" cy="24" r="18" fill="black" fill-opacity="0.5"/>
                                <path fill-rule="evenodd" clip-rule="evenodd"
                                      d="M29.7071 19.7071C30.0976 19.3166 30.0976 18.6834 29.7071 18.2929C29.3166 17.9024 28.6834 17.9024 28.2929 18.2929L24 22.5858L19.7071 18.2929C19.3166 17.9024 18.6834 17.9024 18.2929 18.2929C17.9024 19.3166 17.9024 18.6834 18.2929 19.7071L22.5858 24L18.2929 28.2929C17.9024 28.6834 17.9024 29.3166 18.2929 29.7071C19.3166 30.0976 18.6834 30.0976 19.7071 29.7071L24 25.4142L28.2929 29.7071C28.6834 30.0976 29.3166 30.0976 29.7071 29.7071C30.0976 29.3166 30.0976 28.6834 29.7071 28.2929L25.4142 24L29.7071 19.7071Z"
                                      fill="white"/>
                            </svg>
                        `;
                        content.appendChild(closeButton);

                        if (desktopImage) {
                            const desktopImg = document.createElement('img');
                            desktopImg.src = desktopImage;
                            desktopImg.alt = 'CES Booth Map';
                            desktopImg.className = 'ces-booth-popup-image ces-booth-popup-image-desktop';
                            content.appendChild(desktopImg);
                        }

                        if (mobileImage) {
                            const mobileImg = document.createElement('img');
                            mobileImg.src = mobileImage;
                            mobileImg.alt = 'CES Booth Map';
                            mobileImg.className = 'ces-booth-popup-image ces-booth-popup-image-mobile';
                            content.appendChild(mobileImg);
                        }

                        popup.appendChild(content);
                        banner.appendChild(popup);

                        // 绑定事件
                        button.addEventListener('click', function(e) {
                            e.preventDefault();
                            popup.classList.add('show');
                            // document.body.style.overflow = 'hidden';
                            requestAnimationFrame(() => {
                                requestAnimationFrame(() => {
                                    popup.classList.add('visible');
                                });
                            });
                        });

                        closeButton.addEventListener('click', function(e) {
                            e.preventDefault();
                            popup.classList.remove('visible');
                            setTimeout(() => {
                                popup.classList.remove('show');
                                document.body.style.overflow = '';
                            }, 300);
                        });

                        overlay.addEventListener('click', function(e) {
                            if (e.target === overlay) {
                                popup.classList.remove('visible');
                                setTimeout(() => {
                                    popup.classList.remove('show');
                                    document.body.style.overflow = '';
                                }, 300);
                            }
                        });
                    }
                }
            });
        });
    }
})();